import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock3, Lightbulb, RotateCcw, CheckCircle2 } from "lucide-react";
import { API } from "./shared";

const box = { border: "1px solid #FDE68A", background: "#FFFBEB", borderRadius: 12, padding: 14, margin: "14px 0" };
const MAX = 2;

function ExtendBox({ job, role, hasBids, refresh, close }) {
  const [busy, setBusy] = useState(false);
  const used = job.extension_count || 0;
  const isAdmin = role === "admin";
  const reopen = job.status === "closed";
  const canExtend = isAdmin || used < MAX;
  const extend = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/jobs/${job.id}/extend`);
      toast.success(r.data.reopened ? "Auction reopened for 48 hours" : `Auction extended by 48 hours (${r.data.extension_count}${isAdmin ? "" : `/${r.data.max_extensions}`})`);
      close?.(); await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not extend"); } finally { setBusy(false); }
  };
  const title = reopen ? "Auction closed, not awarded" : hasBids ? `${job.bid_count || "Bids"} received · need more time?` : "No bids yet";
  const text = reopen ? "Reopen it for 48 hours. Existing bids are kept and every supplier is re-alerted."
    : hasBids ? "Extend by 48 hours to attract more offers. Bidders are told and can sharpen their price; matching suppliers are re-alerted."
    : `If the window ends without bids we extend it by 48 hours automatically and re-alert more printers, up to ${MAX} times. This never uses an extra RFQ.`;
  return (
    <div data-testid="no-bid-extend-box" style={box}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 700, fontSize: 13 }}><Clock3 size={15}/>{title}</div>
      <div style={{ fontSize: 12.5, color: "#78350F", margin: "4px 0 10px" }}>
        {text}
        <span data-testid="no-bid-extension-count" style={{ display: "block", marginTop: 4, fontWeight: 700 }}>Extensions used: {used}{isAdmin ? " (admin: no limit)" : `/${MAX}`}</span>
      </div>
      {canExtend ? <button data-testid="no-bid-extend-btn" className="so-btn so-blue" disabled={busy} onClick={extend}>{busy ? "Working…" : reopen ? "Reopen for 48 hours" : "Extend by 48 hours"}</button>
        : <span data-testid="extend-limit-reached" style={{ fontSize: 12, color: "#92400E" }}>Limit reached. Contact GoPrintPack support if you need more time.</span>}
    </div>
  );
}

function RelistBox({ job, refresh, close }) {
  const [tips, setTips] = useState([]);
  const [needBy, setNeedBy] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/jobs/${job.id}/no-bid-tips`).then(r => setTips(r.data.tips)).catch(() => {}); }, [job.id]);
  const relist = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/relist`, { need_by_date: needBy || null, buyer_note: note || null });
      toast.success("Relisted · sent for quick review, no extra RFQ used");
      close?.(); await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not relist"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="no-bid-relist-box" style={box}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 700, fontSize: 13 }}><Lightbulb size={15}/>Closed with no bids. Tips to get bids next time:</div>
      <ul data-testid="no-bid-tips" style={{ fontSize: 12.5, color: "#78350F", margin: "6px 0 10px 18px" }}>{tips.map(t => <li key={t}>{t}</li>)}</ul>
      <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 8 }}>
        <input data-testid="relist-need-by" type="date" min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)} value={needBy} onChange={e => setNeedBy(e.target.value)} style={{ border: "1px solid #CBD5E1", borderRadius: 8, padding: "6px 8px", fontSize: 12.5 }} title="New need-by date (optional)"/>
        <input data-testid="relist-note" value={note} onChange={e => setNote(e.target.value)} placeholder="Note for printers (optional), e.g. flexible on finish" style={{ border: "1px solid #CBD5E1", borderRadius: 8, padding: "6px 8px", fontSize: 12.5 }}/>
      </div>
      <button data-testid="relist-btn" className="so-btn so-blue" style={{ marginTop: 10 }} disabled={busy} onClick={relist}><RotateCcw size={14}/>{busy ? "Relisting…" : "Relist this job"}</button>
    </div>
  );
}

export function NoBidPanel({ job, bids, role, refresh, close }) {
  if (!["buyer", "admin"].includes(role) || job.awarded_supplier_id) return null;
  const hasBids = (bids || []).length > 0;
  const admin = role === "admin";
  if (job.status === "live") return <ExtendBox job={job} role={role} hasBids={hasBids} refresh={refresh} close={close}/>;
  if (job.status !== "closed") return null;
  return (
    <>
      {admin && <ExtendBox job={job} role={role} hasBids={hasBids} refresh={refresh} close={close}/>}
      {!hasBids && job.no_bid_closed && <RelistBox job={job} refresh={refresh} close={close}/>}
    </>
  );
}

export function AcNoBidFollowup() {
  const [rows, setRows] = useState(null);
  const load = () => axios.get(`${API}/admin/jobs/no-bid-followup`).then(r => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const done = async (id) => {
    try { await axios.post(`${API}/admin/jobs/${id}/followup-done`); toast.success("Marked as followed up"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!rows) return <p style={{ color: "#64748b" }}>Loading…</p>;
  return (
    <div data-testid="ac-no-bid-followup">
      <p style={{ fontSize: 12.5, color: "#64748b", margin: "0 0 12px" }}>Auctions that got no bids. Call printers who fit these jobs, then mark them done.</p>
      {!rows.length ? <p data-testid="no-bid-empty" style={{ color: "#64748b" }}>Nothing to follow up. Every auction has bids.</p> : (
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead style={{ background: "#F8FAFC", textAlign: "left" }}><tr>{["Job", "Buyer", "Status", "Extensions", "Window ends", "Printers re-alerted", ""].map(h => <th key={h} style={{ padding: "9px 12px" }}>{h}</th>)}</tr></thead>
            <tbody>{rows.map(j => (
              <tr key={j.id} data-testid={`no-bid-row-${j.id}`} style={{ borderTop: "1px solid #EEF2F7" }}>
                <td style={{ padding: "9px 12px" }}><b>{j.title}</b><div style={{ color: "#64748b", fontSize: 11 }}>{j.followup_reason}</div></td>
                <td style={{ padding: "9px 12px" }}>{j.buyer_name || "—"}</td>
                <td style={{ padding: "9px 12px" }}>{j.status === "live" ? "Live (extended)" : "Closed, no bids"}</td>
                <td style={{ padding: "9px 12px" }}>{j.extension_count || 0}/{MAX}</td>
                <td style={{ padding: "9px 12px" }}>{j.auction_ends_at ? new Date(j.auction_ends_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</td>
                <td style={{ padding: "9px 12px" }}>{j.last_realert_count ?? "—"}</td>
                <td style={{ padding: "9px 12px" }}><button data-testid={`no-bid-done-${j.id}`} className="so-btn" onClick={() => done(j.id)}><CheckCircle2 size={14}/>Done</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
